import { Skeleton, SkeletonButton, SkeletonText } from '@/components/ui/skeleton';

interface PageHeaderSkeletonProps {
    readonly actionCount?: number;
    readonly showBackButton?: boolean;
    readonly showDescription?: boolean;
}

/** Trailing action widths, roughly matching real button labels. */
const ACTION_WIDTHS = [120, 80, 70, 80] as const;

/**
 * Loading placeholder for a plain page header — title, optional caption, and
 * right-aligned actions, with no leading icon tile.
 *
 * For the module list headers that DO open with a 56px icon tile (Sequences,
 * Workflows, Polls, Contacts), use `ModuleHeaderSkeleton` instead: it reserves
 * the tile, without which the title slides left on load.
 */
export function PageHeaderSkeleton({
    actionCount = 0,
    showBackButton = false,
    showDescription = true,
}: PageHeaderSkeletonProps) {
    return (
        <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
                {showBackButton && <Skeleton className="size-8 shrink-0 rounded-md" />}
                <div className="flex flex-col">
                    <SkeletonText size="h1" width="192px" />
                    {showDescription && <SkeletonText size="sm" width="288px" className="mt-0.5" />}
                </div>
            </div>
            {actionCount > 0 && (
                <div className="flex items-center gap-2">
                    {Array.from({ length: actionCount }).map((_, i) => (
                        <SkeletonButton
                            key={i}
                            size="sm"
                            width={ACTION_WIDTHS[i % ACTION_WIDTHS.length]}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
